import { useState } from "react";
import { Alert, Dialog, Button, Row, TextInput } from "@narmi/design_system";

import { CSRF_TOKEN } from "@pages/csrf";

import { AdminApiSchema } from "../../api_utils";
import { useInstitutionContext } from "@hooks/institutionContext";

type FedNowTransaction = AdminApiSchema.components["schemas"]["FedNowTransactionAdmin"];

export default function DeclineOverrideDialog({
  transaction,
  isOpen,
  setIsOpen,
  successCallback,
}: {
  transaction: FedNowTransaction;
  isOpen: boolean;
  setIsOpen: CallableFunction;
  successCallback: CallableFunction;
}) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const { institution } = useInstitutionContext();

  const onSubmit = async () => {
    setDisableSubmit(true);
    const response = await fetch(`/v2/admin/transfers/instant/${transaction.id}/override/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": CSRF_TOKEN,
      },
      body: JSON.stringify({ note: note }),
    });
    if (!response.ok) {
      const errData = await response.json();
      if (typeof errData.message === "string") {
        setError(errData.message);
      } else {
        setError(String(Object.values(errData)[0]));
      }
      return;
    }
    successCallback();
    setIsOpen(false);
    setDisableSubmit(false);
  };

  return (
    <Dialog isOpen={isOpen} title="Override decline" onUserDismiss={() => setIsOpen(false)}>
      {error && (
        <div className="padding--bottom--s">
          <Alert isActive kind="error">
            {error}
          </Alert>
        </div>
      )}
      <Alert isActive isDismissable={false} kind="warn" icon="alert-triangle">
        FedNow payments are irrevocable.
      </Alert>
      <p className="padding--y">
        Make sure you review all money movement transactions prior to overriding the decline.
        Confirm all details with banking{" "}
        {institution.institution_type === "credit_union" ? "member" : "customer"} before proceeding.
      </p>

      <div className="padding--bottom">
        <h4 className="padding--bottom--s nds-sans">Additional information</h4>
        <TextInput
          label="Provide a narrative or additional details"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNote(event.target.value)}
        />
      </div>

      <Row justifyContent="end" alignItems="center">
        <Row.Item>
          <Button
            label="Cancel"
            kind="plain"
            onClick={() => setIsOpen(false)}
            disabled={disableSubmit}
          />
        </Row.Item>
        <Row.Item shrink>
          <Button label="Confirm" onClick={onSubmit} disabled={disableSubmit} />
        </Row.Item>
      </Row>
    </Dialog>
  );
}
